// public/v3/src/lib/apcm-suggest.js — pure-function APCM tier heuristic.
// Mirrors ai-suggest.js posture: no LLM call, no I/O.
//
// Input: patient blob (raw or adapter-wrapped via _raw).
// Output: { code, label, reason, dualEligible }
//
// Rules (from NACHC March 2026 + CMS PFS 2026):
//   - G0558 Level 3: ≥2 chronic + QMB (operator-confirmed via data.qmb.confirmed
//     OR derived via dual-eligible Medicare+Medicaid on file)
//   - G0557 Level 2: ≥2 chronic, no QMB signal
//   - G0556 Level 1: 0 or 1 chronic conditions (NACHC: "zero or one chronic")
//
// Every patient on the Fallback page is CCM-enrolled by construction, which
// satisfies the medical-necessity judgment APCM requires even at 0-chronic.

import { chronicCount } from './ai-suggest';
import { inferQmbStatus, isFreshQmb, isMedicaidInsurance, isMedicareInsurance } from './eligibility-infer';

function readRaw(patient) {
  return patient?._raw || patient || {};
}

export function hasMedicaid(patient) {
  const ins = readRaw(patient).insurances || [];
  return ins.some(isMedicaidInsurance);
}

export function hasMedicare(patient) {
  const ins = readRaw(patient).insurances || [];
  return ins.some(isMedicareInsurance);
}

export function hasFreshQmbConfirmation(patient) {
  const raw = readRaw(patient);
  if (isFreshQmb(raw.qmb)) return true;
  return inferQmbStatus(patient).confirmed === true;
}

export function suggestApcmTier(patient) {
  const n = chronicCount(patient);
  const dualEligible = hasMedicaid(patient) && hasMedicare(patient);
  const inferredQmb = inferQmbStatus(patient);
  const qmbConfirmed = hasFreshQmbConfirmation(patient);

  if (qmbConfirmed && n >= 2) {
    return {
      code: 'G0558',
      label: 'G0558 Level 3',
      reason: inferredQmb.source === 'manual'
        ? '2+ chronic + QMB confirmed by operator (HETS/MAC)'
        : `2+ chronic + QMB confirmed via Elation eligibility (${inferredQmb.source})`,
      dualEligible,
    };
  }
  if (n >= 2) {
    return {
      code: 'G0557',
      label: 'G0557 Level 2',
      reason: inferredQmb.dualSignal || dualEligible
        ? '2+ chronic + Medicare/Medicaid signal — confirm QMB via Elation eligibility to upgrade to G0558'
        : '2+ chronic — confirm QMB via Elation eligibility to upgrade to G0558',
      dualEligible,
    };
  }
  return {
    code: 'G0556',
    label: 'G0556 Level 1',
    reason: n === 1
      ? '1 chronic condition'
      : '0 chronic conditions — APCM at provider discretion',
    dualEligible: false,
  };
}
